import React, { memo, useEffect, useRef, useState } from 'react';
import { Player, PlayerEvent } from 'bitmovin-player';
import {
  UIFactory,
  UIManager,
  UIContainer,
  Button,
  ControlBar,
  Container,
  PlaybackTimeLabel,
  PlaybackTimeLabelMode,
  SeekBarLabel,
  SeekBar,
  PlaybackToggleButton,
  FullscreenToggleButton,
  VolumeToggleButton,
  VolumeSlider,
  Spacer,
  BufferingOverlay,
  PlaybackToggleOverlay,
  CastStatusOverlay,
  TitleBar,
  RecommendationOverlay,
  Watermark,
  ErrorMessageOverlay,
  SubtitleOverlay,
  SettingsPanelPage,
  SettingsPanelItem,
  VideoQualitySelectBox,
  PlaybackSpeedSelectBox,
  AudioQualitySelectBox,
  SettingsPanel,
  SubtitleSettingsPanelPage,
  // SubtitleSelectBox,
  SettingsPanelPageOpenButton,
  // SubtitleSettingsLabel,
  SettingsToggleButton,
  SubtitleListBox,
  AudioTrackListBox,
  PictureInPictureToggleButton,
  AirPlayToggleButton,
  CastToggleButton,
  VRToggleButton
} from 'bitmovin-player-ui';
import 'bitmovin-player-ui/dist/css/bitmovinplayer-ui.min.css';
import { setCurrentTime, setPlayerRef, setSeekVideoShot } from 'store/videoSeektimeSlice';
import { useDispatch, useSelector } from 'react-redux';
import api from 'services/api';
import { useParams } from 'react-router-dom';
// import SubtitlesModule from 'bitmovin-player/modules/bitmovinplayer-subtitles';
// import SubtitlesCEA608Module from 'bitmovin-player/modules/bitmovinplayer-subtitles-cea608';
import useActiveWorkspace from 'hooks/useActiveWorkspace';
import { RootState } from 'store';
import { setPlayerTime } from 'store/secondaryViewSlice';
import { getSecondaryOpenVideo } from 'store/secondaryViewSlice';
import { isMobile } from 'react-device-detect';

interface IBitmovin {
  cinemaViewport: boolean;
  setCinemaViewport: any;
  currentVideo?: any;
  videoShot: any;
  setHovered?: any;
  channelHashId?: string;
  timestamp?: any;
}

function BitmovinPlayer({
  cinemaViewport,
  setCinemaViewport,
  currentVideo,
  videoShot,
  setHovered,
  channelHashId,
  timestamp
}: IBitmovin) {
  const dispatch = useDispatch();
  const playerRef = useRef(null as any);
  const currentUI = useRef(null as any);
  const playerDiv = useRef(document.createElement('div'));
  const startTime = useRef(0);
  const endTime = useRef(0);
  const prevTotalTime = useRef(0);
  const [totalTime, setTotalTime] = useState(0);
  const { videoId } = useParams();
  const [status, setStatus] = useState(null as any);
  const { activeWorkspace } = useActiveWorkspace();
  const isExternal = useSelector((state: RootState) => state.app.external);
  const externalToken = localStorage.getItem('externalToken');
  const userToken = isExternal ? externalToken : activeWorkspace.user.access;
  const secondaryViewVideos = useSelector((state: RootState) => state.secondaryView);

  const [secondaryVideo, setSecondaryVideo] = useState<null | any>(null);

  useEffect(() => {
    if (channelHashId) {
      const video = getSecondaryOpenVideo(secondaryViewVideos, channelHashId);
      if (video) {
        setSecondaryVideo(video);
      }
    }
  }, [channelHashId]);

  const playerConfig = {
    key: `${process.env.REACT_APP_BIT_MOVIN_KEY}`,
    ui: false,
    network: {
      preprocessHttpRequest: function (type: any, request: any) {
        if (type == 'key/hls/aes') {
          request.headers['Authorization'] = `Bearer ${userToken}`;
        }
        return Promise.resolve(request);
      }
    }
  };

  const createUIContainer = (inFullMode?: boolean) => {
    const cinemaView = new Button({
      cssClass: `${cinemaViewport ? 'ui-cinemaView' : 'ui-defaultView'} bmpui-ui-button`
    });
    cinemaView.onClick.subscribe(function () {
      setCinemaViewport((prev: boolean) => !prev);
    });
    cinemaView.setAriaLabel(`${cinemaViewport ? 'Default View' : 'Cinema View'}`);
    cinemaView.setText(`${cinemaViewport ? 'Default View' : 'Cinema View'}`);

    if (inFullMode || setHovered) {
      cinemaView.hide();
    } else {
      cinemaView.show();
    }

    const subtitleOverlay = new SubtitleOverlay();

    const settingsPanel = new SettingsPanel({
      components: [
        new SettingsPanelPage({
          components: [
            new SettingsPanelItem('Video Quality', new VideoQualitySelectBox()),
            new SettingsPanelItem('Speed', new PlaybackSpeedSelectBox()),
            new SettingsPanelItem('Audio Quality', new AudioQualitySelectBox())
          ]
        })
      ],
      hidden: true
    });

    const subtitleListBox = new SubtitleListBox();

    const subtitleListBoxSettingsPanelPage = new SettingsPanelPage({
      components: [new SettingsPanelItem(null as any, subtitleListBox)]
    });

    const subtitleSettingsPanel = new SettingsPanel({
      components: [subtitleListBoxSettingsPanelPage],
      hidden: true,
      pageTransitionAnimation: false
    });

    const subtitleSettingsPanelPage = new SubtitleSettingsPanelPage({
      settingsPanel: subtitleSettingsPanel,
      overlay: subtitleOverlay
    });

    const subtitleSettingsOpenButton = new SettingsPanelPageOpenButton({
      targetPage: subtitleSettingsPanelPage,
      container: subtitleSettingsPanel,
      text: 'Settings',
      cssClasses: ['customization-open-button']
    });

    subtitleListBoxSettingsPanelPage.addComponent(
      new SettingsPanelItem(null as any, subtitleSettingsOpenButton, {
        cssClasses: ['subtitle-customization-settings-panel-item']
      })
    );
    subtitleSettingsPanel.addComponent(subtitleSettingsPanelPage);

    const audioTrackListBox = new AudioTrackListBox();
    const audioTrackSettingsPanel = new SettingsPanel({
      components: [
        new SettingsPanelPage({
          components: [new SettingsPanelItem(null as any, audioTrackListBox)]
        })
      ],
      hidden: true
    });

    const controlBar = new ControlBar({
      components: [
        audioTrackSettingsPanel,
        subtitleSettingsPanel,
        settingsPanel,
        new Container({
          components: [
            new PlaybackTimeLabel({
              timeLabelMode: PlaybackTimeLabelMode.CurrentTime,
              hideInLivePlayback: true
            }),
            new SeekBar({ label: new SeekBarLabel() }),
            new PlaybackTimeLabel({
              timeLabelMode: PlaybackTimeLabelMode.TotalTime,
              cssClasses: ['text-right']
            })
          ],
          cssClasses: ['controlbar-top']
        }),
        new Container({
          components: [
            new PlaybackToggleButton(),
            new VolumeToggleButton(),
            new VolumeSlider(),
            new Spacer(),
            new PictureInPictureToggleButton(),
            new AirPlayToggleButton(),
            new CastToggleButton(),
            new VRToggleButton(),
            new SettingsToggleButton({
              settingsPanel: audioTrackSettingsPanel,
              cssClass: 'ui-audiotracksettingstogglebutton'
            }),
            new SettingsToggleButton({
              settingsPanel: subtitleSettingsPanel,
              cssClass: 'ui-subtitlesettingstogglebutton'
            }),
            cinemaView,
            new SettingsToggleButton({ settingsPanel: settingsPanel }),
            new FullscreenToggleButton()
          ],
          cssClasses: ['controlbar-bottom']
        })
      ]
    });

    return new UIContainer({
      components: [
        subtitleOverlay,
        new BufferingOverlay(),
        new PlaybackToggleOverlay(),
        new CastStatusOverlay(),
        controlBar,
        new TitleBar(),
        new RecommendationOverlay(),
        new Watermark(),
        new ErrorMessageOverlay()
      ]
    });
  };

  useEffect(() => {
    const playerInstance = new Player(playerDiv.current, playerConfig);

    playerRef.current = playerInstance;
    // UIFactory.buildDefaultUI(playerInstance);
    // const mySimpleUi = createUIContainer();
    // uiManagerRef.current = UIFactory.buildDefaultUI(playerInstance); // Build UI initially

    currentUI.current = UIFactory.buildDefaultUI(playerInstance);
    currentUI.current.currentUi.onControlsShow.subscribe(() => {
      if (setHovered) {
        setHovered(true);
      }
    });
    currentUI.current.currentUi.onControlsHide.subscribe(() => {
      if (setHovered) {
        setHovered(false);
      }
    });
    playerInstance
      .load({
        hls: videoShot.hls_location
      })
      .then(() => {
        playerInstance.subtitles.add({
          id: videoShot.id,
          url: videoShot.transcript, // URL to your closed captions file (e.g., VTT file)
          kind: 'captions',
          label: 'English', // Caption label
          lang: 'en' // Language of the captions
        });
      });

    playerInstance.on(PlayerEvent.SourceLoaded, () => {
      dispatch(setPlayerRef(playerInstance));
    });

    playerInstance.on(PlayerEvent.Ready, (event: any) => {
      if (timestamp) {
        playerInstance.seek(timestamp);
      }
    });

    playerInstance.on(PlayerEvent.Playing, (event: any) => {
      setStatus('playing');
      startTime.current = event.time;
      dispatch(setCurrentTime({ currentTime: event.time }));
    });

    playerInstance.on(PlayerEvent.Seek, (event: any) => {
      dispatch(setCurrentTime({ currentTime: event.seekTarget }));
    });
    playerInstance.on(PlayerEvent.Paused, (event: any) => {
      setStatus('paused');
      endTime.current = event.time;
    });

    playerInstance.on(PlayerEvent.PlaybackFinished, () => {
      setStatus('playbackfinished');
      endTime.current = playerInstance.getDuration();
    });

    playerInstance.on(PlayerEvent.TimeChanged, (event: any) => {
      // dispatch(setCurrentTime({ currentTime: event.time }));
    });

    return () => {
      const currentTime = playerInstance.getCurrentTime();
      dispatch(setPlayerTime({ channelHashId, currentTime }));
      dispatch(setPlayerRef(null));
      dispatch(setSeekVideoShot({ time: 0, videoShotIdx: 0 }));
      playerInstance.destroy();
      currentUI.current?.release();
    };
  }, []);

  useEffect(() => {
    if (playerRef.current) {
      playerRef.current.on(PlayerEvent.Ready, () => {
        if (secondaryVideo && secondaryVideo.playerTime) {
          playerRef.current.seek(secondaryVideo.playerTime);
        }
      });
    }
  }, [secondaryVideo, playerRef.current]);

  // useEffect(() => {
  //   currentUI.current?.release();
  //   // const mySimpleUi = createUIContainer();
  //   // currentUI.current = new UIManager(playerRef.current, mySimpleUi);
  //   currentUI.current.currentUi.onControlsShow.subscribe(() => {
  //     if (setHovered) {
  //       setHovered(true);
  //     }
  //   });
  //   currentUI.current.currentUi.onControlsHide.subscribe(() => {
  //     if (setHovered) {
  //       setHovered(false);
  //     }
  //   });
  //   playerRef.current.on(PlayerEvent.ViewModeChanged, (data: any) => {
  //     let mySimpleUi;
  //     if (data.to === 'inline') {
  //       mySimpleUi = createUIContainer(false);
  //     } else {
  //       mySimpleUi = createUIContainer(true);
  //     }
  //     currentUI.current?.release();
  //     currentUI.current = new UIManager(playerRef.current, mySimpleUi);
  //   });
  // }, [cinemaViewport]);

  useEffect(() => {
    if (playerRef.current) {
      const container = document.querySelector('.bmpui-controlbar-bottom .bmpui-container-wrapper ');
      if (container) {
        const button = document.createElement('button');
        button.id = 'cinema-view';
        button.setAttribute('aria-label', 'Cinema View');
        button.className = 'bmpui-ui-cinemaview';
        button.type = 'button';
        button.setAttribute('aria-pressed', 'false');
        button.tabIndex = 0;
        button.role = 'button';
        // button.style.backgroundImage = "url('/public/assets/img/theater-mode.svg')";
        button.addEventListener('click', () => {
          // Handle the button click event
          const parentContainer = document.getElementById('parent');
          parentContainer?.classList.toggle('cinema-mode');
        });
        container.appendChild(button);
      }

      playerRef.current.load({ hls: videoShot.hls_location }).then(() => {
        playerRef.current.subtitles.add({
          id: videoShot.id,
          url: videoShot.transcript, // URL to your closed captions file (e.g., VTT file)
          kind: 'captions',
          label: 'English', // Caption label
          lang: 'en' // Language of the captions
        });
      });
    }
  }, [videoShot]);

  useEffect(() => {
    if (status !== 'playing' && endTime.current - startTime.current > 0) {
      setTotalTime((prev) => prev + endTime.current - startTime.current);
    }
  }, [status]);

  useEffect(() => {
    if (totalTime - prevTotalTime.current > 10 && currentVideo?.viewer) {
      api.post(`api/videos/${videoId}/external-view/`, {
        viewer: currentVideo.viewer,
        recordingViewDurationInMinutes: totalTime
      });
    }
    prevTotalTime.current = totalTime;
  }, [totalTime]);

  console.log('Total Duration', totalTime);

  return <div id="player" className={`max-h-[calc(100vh-92px)] ${isMobile ? '' : 'rounded-md'}`} ref={playerDiv} />;
}

export default memo(BitmovinPlayer);
